import { useState, useEffect } from "react";
import { Heart, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PRESET_AMOUNTS = [10, 25, 50, 100];

interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DonateModal = ({ isOpen, onClose }: DonateModalProps) => {
  const [selected, setSelected] = useState<number | null>(25);
  const [custom, setCustom] = useState("");
  const [isRecurring, setIsRecurring] = useState(true); // Monthly preselected for long-term support
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelected(25);
      setCustom("");
      setIsRecurring(true);
      setIsRedirecting(false);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const amount = custom ? parseFloat(custom) : selected;

  const handleDonate = async () => {
    if (!amount || amount < 1) return;

    setIsRedirecting(true);
    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Math.round(amount * 100), recurring: isRecurring }),
      });

      if (!res.ok) throw new Error("Failed to create session");

      const { url } = await res.json() as { url: string };
      window.location.href = url;
    } catch (err) {
      console.error("Donation error:", err);
      setIsRedirecting(false);
    }
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setCustom(val);
      setSelected(null);
    }
  };

  const handlePreset = (amt: number) => {
    setSelected(amt);
    setCustom("");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-60 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="fixed inset-0 z-60 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="donate-modal-title"
              className="pointer-events-auto bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative"
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-4">
                  <Heart className="w-7 h-7 text-primary" fill="currentColor" />
                </div>
                <h2 id="donate-modal-title" className="text-2xl font-semibold text-gray-900">
                  Support Our Mission
                </h2>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                  Your donation helps us bring free Python education to kids everywhere.
                  Byte-Sized Python is a 501(c)(3) nonprofit.
                </p>
              </div>

              {/* One-time / Monthly toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 mb-2 bg-gray-100 rounded-xl">
                <button
                  onClick={() => setIsRecurring(false)}
                  className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    !isRecurring ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  One-time
                </button>
                <button
                  onClick={() => setIsRecurring(true)}
                  className={`relative py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    isRecurring ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Monthly
                  <span className="absolute -top-2 -right-1 px-1.5 py-0.5 rounded-full bg-secondary text-[9px] font-bold text-gray-900 leading-none shadow-sm">
                    ♥ most impact
                  </span>
                </button>
              </div>
              <p className="text-center text-xs text-gray-400 mb-4 h-4">
                {isRecurring
                  ? "A recurring gift keeps free coding education going all year."
                  : "A single gift, given once."}
              </p>

              {/* Preset amounts */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {PRESET_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => handlePreset(amt)}
                    className={`py-3 rounded-xl font-semibold text-sm border-2 transition-all duration-200 ${
                      selected === amt
                        ? "border-primary bg-primary text-white shadow-md"
                        : "border-gray-200 text-gray-700 hover:border-primary hover:text-primary"
                    }`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div className="relative mb-6">
                <label htmlFor="custom-amount" className="sr-only">
                  Custom donation amount
                </label>
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                <input
                  id="custom-amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Custom amount"
                  value={custom}
                  onChange={handleCustomChange}
                  onFocus={() => setSelected(null)}
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary text-gray-800 placeholder-gray-400 transition-colors"
                />
              </div>

              {/* Donate button */}
              <button
                onClick={handleDonate}
                disabled={!amount || amount < 1 || isRedirecting}
                className="w-full py-4 rounded-xl bg-primary text-white font-semibold text-base hover:bg-primary/80 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary/30"
              >
                <Heart className="w-5 h-5" fill="currentColor" />
                {isRedirecting
                  ? "Redirecting to Stripe…"
                  : amount && amount >= 1
                  ? `Donate $${Number.isInteger(amount) ? amount : amount.toFixed(2)}${isRecurring ? "/mo" : ""}`
                  : "Enter an amount"}
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                Secured by{" "}
                <span className="font-semibold text-[#635bff]">Stripe</span>. Tax-deductible to the extent permitted by law.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
